// src/components/common/TimeRangePicker.tsx

export type TimeRangeOption = '1d' | '7d' | '30d' | '90d' | '180d' | '365d' | 'All';

export type TimeRangeTheme = 'default' | 'liquidity' | 'alerts' | 'blue';

interface TimeRangePickerProps<T extends string> {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  theme?: TimeRangeTheme;
}

/**
 * Standardized Time Range Picker Component
 * 
 * Consistent styling across the app:
 * - Text size: text-[12px] (consistent across all themes)
 * - Padding: px-2.5 py-1 (default, liquidity, blue) / px-3 py-1 (alerts)
 * - Border radius: rounded-md (buttons), rounded-lg (container) / rounded-xl (alerts)
 * - Four themes: default, liquidity, alerts, blue
 * - All themes now use same compact size except alerts which is slightly larger
 */
function TimeRangePicker<T extends string>({ 
  options, 
  value, 
  onChange,
  className = "",
  theme = 'default'
}: TimeRangePickerProps<T>) {
  // Theme-specific styles
  const themeStyles = {
    default: {
      container: 'bg-gray-800/50 border-gray-700/40 rounded-lg',
      button: 'px-2.5 py-1 rounded-md',
      active: 'bg-slate-600/30 text-slate-200',
      inactive: 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
    },
    liquidity: {
      container: 'bg-gray-800/50 border-gray-700/40 rounded-lg',
      button: 'px-2.5 py-1 rounded-md',
      active: 'bg-slate-600/30 text-slate-200',
      inactive: 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
    },
    alerts: {
      container: 'bg-gray-800/50 border-gray-700/50 rounded-lg',
      button: 'px-2.5 py-1 rounded-md',
      active: 'bg-amber-500/20 text-amber-300 shadow-sm',
      inactive: 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/30'
    },
    blue: {
      container: 'bg-gray-800/50 border-gray-700/50 rounded-lg',
      button: 'px-2.5 py-1 rounded-md',
      active: 'bg-blue-500/20 text-blue-300 shadow-sm',
      inactive: 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/30'
    }
  };

  const styles = themeStyles[theme];

  return (
    <div className={`flex items-center ${styles.container} p-1 border ${className}`}>
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`${styles.button} text-[12px] font-medium transition-all duration-200 ${
            value === option 
              ? styles.active
              : styles.inactive
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export default TimeRangePicker;
